import { useState } from 'react';
import copy from 'copy-to-clipboard';
import { Controller } from 'react-hook-form';
import Autocomplete from '../../autocomplete/autocomplete';
import Select from '../../select/select';
import DateInput from '../../date-input/date-input';
import Textarea from '../../textarea/textarea';
import ToggleInput from '../../checkbox/toggle';
import Checkbox from '../../checkbox/checkbox';
import { LockReasonEnum, LockReasonTitle } from '../types';
import { LockFormTypes } from './types';
import IconCopyTwo from '../../icons/copy-two';

import cn from './styles.module.css';

let searchTimeout: NodeJS.Timeout;
let usersSearchTimeout: NodeJS.Timeout;

export default function LockForm({
  control,
  errors,
  organizations,
  organizationsPagination,
  nextPageIsLoading,
  getOrganizations,
  setSearchText,
  organizationsIsLoading,
  isEditable,
  lockId,
  searchText,
  setValue,
  employees,
  users,
  usersPagination,
  nextUsersPageIsLoading,
  getUsers,
  setUsersSearchText,
  usersIsLoading,
  usersSearchText,
  withoutUserSelector,
  userId,
}: LockFormTypes) {
  const [userType, setUserType] = useState<
    'organizations' | 'users' | 'employees'
  >('organizations');
  const [copySuccess, setCopySuccess] = useState(false);

  const onCopy = (id: string) => {
    copy(id);
    setCopySuccess(true);

    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  };

  return (
    <div className={cn.formWrapper}>
      {lockId && (
        <>
          <div className={cn.formGroup}>
            <Controller
              control={control}
              name="locked"
              render={({ field: { value, onChange, name } }) => (
                <ToggleInput
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  title="Locked"
                  name={name}
                />
              )}
            />
          </div>
          {userId && (
            <Controller
              control={control}
              name="userId"
              render={({ field: { value } }) => (
                <div className={cn.lockUserId}>
                  {`User ID: ${value}`}
                  <button
                    type="button"
                    className={`${cn.copyButton} ${
                      copySuccess ? cn.copySuccess : ''
                    }`}
                    onClick={() => onCopy(value as string)}
                  >
                    <IconCopyTwo width={16} height={16} />
                  </button>
                </div>
              )}
            />
          )}
        </>
      )}
      {!withoutUserSelector && (
        <div
          className={`${cn.userTypeSelectorWrapper} ${
            !lockId ? cn.withBorders : ''
          }`}
        >
          {!lockId && (
            <div className={cn.userTypeSelectorButtons}>
              <div className={cn.userType}>
                <Checkbox
                  type="radio"
                  name="organizations"
                  checked={userType === 'organizations'}
                  title="Organizations"
                  onChange={() => {
                    if (userType !== 'organizations') {
                      setUserType('organizations');

                      setValue('userId', '');

                      if (usersSearchText && setUsersSearchText && getUsers) {
                        setUsersSearchText('');
                        getUsers({});
                      }
                    }
                  }}
                />
              </div>
              <div className={cn.userType}>
                <Checkbox
                  type="radio"
                  name="users"
                  checked={userType === 'users'}
                  title="Users"
                  onChange={() => {
                    if (userType !== 'users') {
                      setUserType('users');

                      setValue('userId', '');

                      if (searchText && setSearchText && getOrganizations) {
                        setSearchText('');
                        getOrganizations({});
                      }
                    }
                  }}
                />
              </div>
              <div className={cn.userType}>
                <Checkbox
                  type="radio"
                  name="employees"
                  checked={userType === 'employees'}
                  title="Employees"
                  onChange={() => {
                    if (userType !== 'employees') {
                      setUserType('employees');

                      setValue('userId', '');

                      if (searchText && setSearchText && getOrganizations) {
                        setSearchText('');
                        getOrganizations({});
                      }

                      if (usersSearchText && setUsersSearchText && getUsers) {
                        setUsersSearchText('');
                        getUsers({});
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}

          {((userType === 'organizations' && !lockId) ||
            (userType === 'organizations' && lockId && userId)) && (
            <Controller
              control={control}
              defaultValue=""
              name="userId"
              render={({ field: { value, name, onChange } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select customer"
                  value={value}
                  title="Customer"
                  onChange={onChange}
                  isLoading={organizationsIsLoading}
                  onSearch={(text) => {
                    if (setSearchText) {
                      setSearchText(text);
                    }

                    clearTimeout(searchTimeout);

                    if (getOrganizations) {
                      searchTimeout = setTimeout(() => {
                        getOrganizations({ searchValue: text });
                      }, 500);
                    }
                  }}
                  list={organizations || []}
                  disabled={Boolean(lockId)}
                  tabIndex={lockId ? -1 : 0}
                  onInfinateScroll={() =>
                    getOrganizations &&
                    getOrganizations({
                      cursor: organizationsPagination?.endCursor,
                      searchValue: searchText,
                    })
                  }
                  hasNextPage={organizationsPagination?.hasNextPage}
                  nextPageIsLoading={nextPageIsLoading}
                  error={errors?.userId?.message}
                  withClearButton={!lockId}
                  withInfiniteScroll
                  fullWidth
                />
              )}
            />
          )}
          {userType === 'users' && (
            <Controller
              control={control}
              defaultValue=""
              name="userId"
              render={({ field: { value, name, onChange } }) => (
                <Autocomplete
                  name={name}
                  placeholder="Select customer"
                  value={value}
                  title="Customer"
                  onChange={onChange}
                  isLoading={usersIsLoading}
                  onSearch={(text) => {
                    if (setUsersSearchText) {
                      setUsersSearchText(text.replace(/\s/g, ''));
                    }

                    clearTimeout(usersSearchTimeout);

                    if (getUsers) {
                      usersSearchTimeout = setTimeout(() => {
                        getUsers({
                          phone: text.replace('+', '').replace(/\s/g, ''),
                        });
                      }, 500);
                    }
                  }}
                  list={users || []}
                  disabled={Boolean(lockId)}
                  tabIndex={lockId ? -1 : 0}
                  onInfinateScroll={() =>
                    getUsers &&
                    getUsers({
                      cursor: usersPagination?.endCursor,
                      phone: usersSearchText,
                    })
                  }
                  hasNextPage={usersPagination?.hasNextPage}
                  nextPageIsLoading={nextUsersPageIsLoading}
                  error={errors?.userId?.message}
                  withClearButton={!lockId}
                  withInfiniteScroll
                  fullWidth
                />
              )}
            />
          )}
          {userType === 'employees' && (
            <Controller
              control={control}
              defaultValue=""
              name="userId"
              render={({ field: { value, name, onChange } }) => (
                <Select
                  name={name}
                  placeholder="Select employee"
                  value={value}
                  title="Employee"
                  list={employees || []}
                  onChange={onChange}
                  error={errors?.userId?.message}
                  withClearButton={!lockId}
                  fullWidth
                />
              )}
            />
          )}
        </div>
      )}
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="reason"
          defaultValue=""
          render={({ field: { value, name, onChange } }) => (
            <Select
              name={name}
              value={value}
              title="Reason"
              onChange={onChange}
              error={errors?.reason?.message}
              placeholder="Select reason"
              disabled={Boolean(lockId && !isEditable)}
              list={[
                {
                  name: LockReasonTitle.USER_TOO_MANY_WRONG_PASSWORDS,
                  value: LockReasonEnum.USER_TOO_MANY_WRONG_PASSWORDS,
                },
                {
                  name: LockReasonTitle.USER_TOO_MANY_WRONG_2FA_CODES,
                  value: LockReasonEnum.USER_TOO_MANY_WRONG_2FA_CODES,
                },
                {
                  name: LockReasonTitle.USER_TOO_MANY_WRONG_PINS,
                  value: LockReasonEnum.USER_TOO_MANY_WRONG_PINS,
                },
                {
                  name: LockReasonTitle.USER_TOO_MANY_VERIFY_SMS,
                  value: LockReasonEnum.USER_TOO_MANY_VERIFY_SMS,
                },
                {
                  name: LockReasonTitle.USER_TOO_MANY_VERIFY_EMAILS,
                  value: LockReasonEnum.USER_TOO_MANY_VERIFY_EMAILS,
                },
                {
                  name: LockReasonTitle.USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS,
                  value: LockReasonEnum.USER_TOO_MANY_VERIFY_PHONE_ATTEMPTS,
                },
                {
                  name: LockReasonTitle.GLOBAL_SYSTEM_DOWNTIME,
                  value: LockReasonEnum.GLOBAL_SYSTEM_DOWNTIME,
                },
                {
                  name: LockReasonTitle.TWILIO_THROTTLE,
                  value: LockReasonEnum.TWILIO_THROTTLE,
                },
                {
                  name: LockReasonTitle.MANUAL,
                  value: LockReasonEnum.MANUAL,
                },
              ]}
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="expiresAt"
          defaultValue=""
          render={({ field: { value, onChange } }) => (
            <DateInput
              title="Expires At"
              value={value}
              placeholder="Select date"
              onChange={(date) => onChange(new Date(date).toISOString())}
              className={cn.securityDatePicker}
              error={errors?.expiresAt?.message}
              disabled={Boolean(lockId && !isEditable)}
              withTime
              withTimeSelector
              withoutCustomSelector
              fullWidth
            />
          )}
        />
      </div>
      <div className={cn.formGroup}>
        <Controller
          control={control}
          name="createdByComment"
          defaultValue=""
          render={({ field: { value, name, onChange } }) => (
            <Textarea
              name={name}
              value={value}
              onChange={onChange}
              title="Comment"
              disabled={Boolean(lockId && !isEditable)}
              placeholder="Enter comment"
              error={errors?.createdByComment?.message}
            />
          )}
        />
      </div>
    </div>
  );
}
